import { Button, Card, Divider, notification, Segmented, Skeleton, Tooltip } from "antd";
import React, { useState } from "react";
import { SmlJsonQueryFilter, SmlJsonQueryOrder } from "@src/types/intelligence-search";
import { SearchResultType } from "../../list";
import { useApiUrl, useCustomMutation } from "@refinedev/core";
import { convertFilterToPayload, useFetchNextOption } from "@src/hooks/intelligence-search/use-fetch-next-options";
import { IntelligenceFormFieldsEnum } from "../../enum";
import { FormInstance } from "antd/lib/form";
import { RawDataInsightMemo } from "../result/raw-data-insight";
import { mergeRequiredFilter } from "@src/util/intelligence-search/merge-required-filter-to-payload";
import { useChartWrapperContext } from "@src/contexts/ab-testing/chart-wrapper-context";
import { MoreAction } from "../more-action";
import { InsightModal } from "../result/insight-modal";
import { CloseCircleOutlined, SyncOutlined } from "@ant-design/icons";
import { orange } from "@ant-design/colors";
import { manualRetryApi } from "@src/util/common/manual-retry-api";
import { css } from "@emotion/react";
import { QueryStatus } from "./query-status";
import { Space } from "antd";
import { TableOutlined, LineChartOutlined } from "@ant-design/icons";
import { AutoGeneratedChartMemo as AutoGeneratedChartMemoV2 } from "../result/auto-generated-chart-v2";
import { NarrativeTitle } from "../result/auto-generated-title";
import { DEFAULT_LIMIT, OrderAndLimitConfig } from "./order-limit";
import { useTracking } from "react-tracking";
import { trackEventPayload } from "@src/util/track-event-payload";
import { SearchComponentSinger } from "./smart-search-template";
import { useWatchFormWithInit } from "@src/hooks/form/use-watch-form-with-init";
import { PERMISSION_EXCLUDED_CHECK, PERMISSION_LAYOUT_CHECK } from "@src/components/crud-permission-layout";
import { config } from "@src/config";

export type DryRunInfo = {
    loading: boolean;
    message?: string[];
    statusCode?: number;
    meta?: Record<string, any>;
};
export const SearchComponent: React.FC<{ form: FormInstance }> = ({ form }) => {
    const { trackEvent } = useTracking();
    const previousQuery = React.useRef<string>();
    const previousQueryLimit = React.useRef<number>();
    const [api, contextHolder] = notification.useNotification();

    const apiUrl = useApiUrl("semantic");
    const filtersWatch: SmlJsonQueryFilter[] =
        useWatchFormWithInit(IntelligenceFormFieldsEnum.FILTERS_SELECTED, form) ?? [];
    const queryOrdersWatch = useWatchFormWithInit(IntelligenceFormFieldsEnum.QUERY_ORDER, form);
    const queryLimitWatch = useWatchFormWithInit(IntelligenceFormFieldsEnum.QUERY_LIMIT, form);

    const [isUserChangeFirstTime, setIsUserChangeFirstTime] = React.useState(false);

    const [viewMode, setViewMode] = React.useState("table");
    const isSetInitialForPreviousFilters = React.useRef(false);

    const [previousFilters, setPreviousFilters] = React.useState<{
        filters: SmlJsonQueryFilter[];
        queryOrders: SmlJsonQueryOrder[];
        queryLimit: number;
    }>();

    const [loadResult, setLoadResult] = useState<SearchResultType>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isQueryComplete, setIsQueryComplete] = useState<boolean>(false);

    const [dryRun, setDryRun] = useState<DryRunInfo>();

    const productCodeWatch = useWatchFormWithInit(IntelligenceFormFieldsEnum.PRODUCT_CODE, form);
    const platformWatch = useWatchFormWithInit(IntelligenceFormFieldsEnum.PLATFORM, form);
    const timeRangeWatch = useWatchFormWithInit(IntelligenceFormFieldsEnum.TIME_RANGE, form);

    const { fetchDryrun } = useFetchNextOption({
        productCode: productCodeWatch,
        startDate: timeRangeWatch?.[0]?.format(config.DATE_FORMAT),
        endDate: timeRangeWatch?.[1]?.format(config.DATE_FORMAT),
    });

    const enoughRequireField =
        !!productCodeWatch && !!platformWatch && platformWatch?.length > 0 && timeRangeWatch?.length > 0;

    const { mutateAsync: loadInsight } = useCustomMutation({
        mutationOptions: {
            retry: 0,
        },
    });
    const customMetricAssetMeta = filtersWatch?.filter((f) => f.isCustomMetric)?.map((f) => f.meta);
    const handleGeneratePayload = ({ filters }: { filters?: SmlJsonQueryFilter[] }) => {
        const _filters = filters ?? filtersWatch ?? [];
        return convertFilterToPayload(
            mergeRequiredFilter({
                platform: platformWatch,
                timeRange: timeRangeWatch,
                selectionFilters: _filters,
            }),
            queryOrdersWatch ?? [],
        );
    };

    React.useEffect(() => {
        if (!filtersWatch?.length && !queryOrdersWatch?.length) return;
        setIsUserChangeFirstTime(true);
    }, [filtersWatch, queryOrdersWatch]);

    const payload = handleGeneratePayload({});

    const isQueryChange =
        previousQuery.current !==
            JSON.stringify({
                productCode: productCodeWatch,
                payload: payload,
            }) || previousQueryLimit.current !== queryLimitWatch;

    React.useEffect(() => {
        setIsQueryComplete(false);
    }, [dryRun]);

    const combineQuery = React.useMemo(() => {
        return {
            filters: filtersWatch,
            queryOrders: queryOrdersWatch,
            queryLimit: queryLimitWatch,
        };
    }, [filtersWatch, queryOrdersWatch, queryLimitWatch]);

    React.useEffect(() => {
        const allowSetPreviousFilters = combineQuery.filters?.length > 0 || combineQuery.queryOrders?.length > 0;
        if (isSetInitialForPreviousFilters.current || !allowSetPreviousFilters) return;

        setPreviousFilters(combineQuery);
        isSetInitialForPreviousFilters.current = true;
    }, [combineQuery]);

    const generatePayload = handleGeneratePayload({ filters: filtersWatch });
    React.useEffect(() => {
        if (!enoughRequireField || !isUserChangeFirstTime) return;
        setDryRun({
            loading: true,
        });
        const timeoutId = setTimeout(() => {
            fetchDryrun(generatePayload)
                .then((res) => {
                    setDryRun({
                        loading: false,
                        meta: res,
                    });
                })
                .catch((err) => {
                    setDryRun({
                        loading: false,
                        statusCode: err?.response?.status,
                        message: err?.response?.data?.detail?.errors ?? [],
                    });
                });
        }, 1000);
        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enoughRequireField, JSON.stringify(generatePayload), isUserChangeFirstTime]);

    const handleLoad = async () => {
        setIsLoading(true);
        trackEvent(trackEventPayload.intelligenceSearchApplySearchClick(productCodeWatch));

        previousQuery.current = JSON.stringify({
            productCode: productCodeWatch,
            payload: payload,
        });
        previousQueryLimit.current = queryLimitWatch;

        setLoadResult(undefined);
        setPreviousFilters(combineQuery);
        const handleLoadInsight = async () => {
            return await loadInsight({
                method: "post",
                url: apiUrl + "/load?limit=" + queryLimitWatch,
                values: payload,
                successNotification: false,
                errorNotification: false,
                config: {
                    headers: {
                        "product-code": productCodeWatch,
                        "start-date": timeRangeWatch?.[0]?.format(config.DATE_FORMAT),
                        "end-date": timeRangeWatch?.[1]?.format(config.DATE_FORMAT),
                    },
                },
            });
        };
        await manualRetryApi({
            cb: handleLoadInsight,
        })
            .then((_result) => {
                setLoadResult(_result?.data as SearchResultType);
            })
            .catch((error) => {
                const networkError = {
                    status: 502,
                    message: "Network error",
                };
                const statusCode = error?.response?.status || networkError.status;
                const messageErr = error?.response?.data?.detail || networkError.message;
                api.open({
                    message: `Error when load result, status code : ${statusCode}`,
                    type: "error",
                    description: messageErr,
                    showProgress: true,
                });
            })
            .finally(() => {
                setIsLoading(false);
                setIsQueryComplete(true);
            });
    };

    const { updateChartState } = useChartWrapperContext();

    React.useEffect(() => {
        updateChartState({
            chartKey: "intelligence-search",
            dataSource: loadResult?.data ?? [],
            modelAlias: "firebase",
            queryOption: {},
            jsonQuery: previousQuery.current ? JSON.parse(previousQuery.current) : undefined,
            renderedQuery: loadResult?.query,
            meta: loadResult?.meta,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadResult?.data, loadResult?.meta, loadResult?.query]);

    if (!enoughRequireField) return null;
    const isDryRunErr = !!dryRun?.message;

    const isDisabled = !isQueryChange || filtersWatch.length === 0;
    const previousFilterLength = previousFilters?.filters?.length ?? 0;
    const isShowWarning = isQueryChange && !isDisabled && previousFilterLength > 0;
    const isTextInsightLoading = dryRun?.loading;

    return (
        <div className="">
            {contextHolder}
            <div className={`${PERMISSION_LAYOUT_CHECK} bg-white z-50 mb-2 `}>
                <div className="flex items-end bg-gray-100/50 border-[1px] border-solid border-gray-100 rounded-md">
                    <SearchComponentSinger
                        productCode={productCodeWatch}
                        startDate={timeRangeWatch?.[0]?.format(config.DATE_FORMAT)}
                        endDate={timeRangeWatch?.[1]?.format(config.DATE_FORMAT)}
                        enoughRequireField={enoughRequireField}
                        form={form}
                        handleGeneratePayload={handleGeneratePayload}
                        setIsUserChangeFirstTime={setIsUserChangeFirstTime}
                    />
                </div>
            </div>
            <div className={`${PERMISSION_LAYOUT_CHECK} mb-2 flex justify-between`}>
                <QueryStatus
                    isUserChangeFirstTime={isUserChangeFirstTime}
                    isLoading={isLoading}
                    isQueryComplete={isQueryComplete}
                    dryRun={dryRun}
                />
                <div className="flex items-center space-x-2 mb-2">
                    <OrderAndLimitConfig filters={filtersWatch} form={form} />
                    <div className="">
                        <Divider className="border-gray-400 mx-3" type="vertical" />
                    </div>

                    <Button
                        loading={isLoading || dryRun?.loading}
                        type="default"
                        onClick={() => {
                            form.setFieldsValue({
                                [IntelligenceFormFieldsEnum.FILTERS_SELECTED]: previousFilters?.filters ?? [],
                                [IntelligenceFormFieldsEnum.QUERY_ORDER]: previousFilters?.queryOrders ?? [],
                                [IntelligenceFormFieldsEnum.QUERY_LIMIT]: previousFilters?.queryLimit ?? DEFAULT_LIMIT,
                            });
                        }}
                        disabled={!previousFilters || JSON.stringify(combineQuery) === JSON.stringify(previousFilters)}
                    >
                        Reset
                    </Button>

                    <Button
                        loading={isLoading || dryRun?.loading}
                        type="default"
                        onClick={() => {
                            form.setFieldsValue({
                                [IntelligenceFormFieldsEnum.FILTERS_SELECTED]: [],
                                [IntelligenceFormFieldsEnum.QUERY_ORDER]: [],
                                [IntelligenceFormFieldsEnum.QUERY_LIMIT]: DEFAULT_LIMIT,
                            });
                        }}
                        disabled={dryRun?.loading || isLoading || filtersWatch.length === 0}
                    >
                        Clear
                    </Button>

                    <Tooltip
                        title={
                            isDryRunErr
                                ? dryRun?.message?.map((msg: string, index: number) => <div key={index}>{msg}</div>)
                                : isShowWarning
                                ? "The data outdated. Click to refresh."
                                : ""
                        }
                        destroyOnHidden
                    >
                        <Button
                            className={PERMISSION_EXCLUDED_CHECK}
                            css={
                                isShowWarning
                                    ? css`
                                          background-color: ${orange[5]};
                                          color: black;
                                          &:hover {
                                              background-color: ${orange[6]} !important;
                                          }
                                      `
                                    : css``
                            }
                            loading={isLoading || dryRun?.loading}
                            type={isDryRunErr ? "default" : "primary"}
                            onClick={() => {
                                if (isDisabled) return;
                                handleLoad();
                            }}
                            disabled={isDisabled || isDryRunErr}
                            icon={isDryRunErr ? <CloseCircleOutlined /> : <SyncOutlined />}
                        >
                            Apply
                        </Button>
                    </Tooltip>
                </div>
            </div>

            <div
                style={{
                    display: isTextInsightLoading ? "block" : "none",
                }}
            >
                <Skeleton active />
            </div>
            <div
                style={{
                    display: isTextInsightLoading ? "none" : "block",
                }}
            >
                <NarrativeTitle
                    customMetricAssetMeta={customMetricAssetMeta?.filter((f) => !!f) ?? []}
                    query={payload}
                    productCode={productCodeWatch}
                    isLoading={isTextInsightLoading}
                    limit={queryLimitWatch}
                />
            </div>
            {isLoading && <Skeleton active />}

            {!!loadResult && !isLoading && (
                <div>
                    <Card>
                        <div className="flex items-center justify-end mb-4">
                            <Space>
                                <Segmented
                                    // vertical
                                    value={viewMode}
                                    options={[
                                        { value: "table", icon: <TableOutlined /> },
                                        { value: "chart", icon: <LineChartOutlined /> },
                                    ]}
                                    onChange={(value) => {
                                        trackEvent(
                                            trackEventPayload.intelligenceSearchModeChartSelect(
                                                productCodeWatch,
                                                `mode ${value}`,
                                            ),
                                        );
                                        setViewMode(value);
                                    }}
                                />
                                <InsightModal form={form} loadResult={loadResult} />
                                <MoreAction form={form} item={["json-query", "rendered-query"]} />
                            </Space>
                        </div>

                        <hr className="border-gray-100 " />
                        {viewMode === "chart" ? (
                            <div>
                                {/* <AutoGeneratedChartMemo data={loadResult?.data} /> */}
                                <AutoGeneratedChartMemoV2 data={loadResult?.data} />
                            </div>
                        ) : (
                            <RawDataInsightMemo
                                loadResult={loadResult}
                                isLoading={isLoading}
                                filters={previousFilters?.filters ?? []}
                            />
                        )}
                    </Card>
                </div>
            )}
        </div>
    );
};
